import CompanyLogo from "@components/company_logo.tsx";
import { Tag } from "@sprout/tag";
import { Link } from "@sprout/typography.tsx";
import Divider from "@sprout/divider.tsx";
import useTranslation from "@/hooks/use_translation";
import RecommendationFeedbackButtons, {
  type RecommendationFeedback,
} from "./recommendation_feedback_buttons.tsx";

const WORK_TYPE_I18N_KEY: Record<string, string> = {
  remote: "workTypeOptions.remote",
  hybrid: "workTypeOptions.hybrid",
  in_person: "workTypeOptions.inPerson",
};

export type JobRecommendation = {
  id: number;
  title: string;
  companyName: string;
  logoUrl: string;
  publicUrl: string;
  viewJobPath: string;
  urlToken: string;
  locations: string[];
  workType: string | null;
  payRanges: string | null;
  viewed: boolean;
  description?: string;
  feedback: RecommendationFeedback | null;
  feedbackPath: string;
};

type Props = {
  job: JobRecommendation;
  onViewJob: (event: React.MouseEvent) => void;
  feedback: RecommendationFeedback | null;
  onFeedback: (next: RecommendationFeedback | null) => void;
  compact?: boolean;
};

const JobRecommendationCard = ({
  job,
  onViewJob,
  feedback,
  onFeedback,
  compact = false,
}: Props) => {
  const { t } = useTranslation("jobSearch");

  const workTypeLabel = job.workType ? t(WORK_TYPE_I18N_KEY[job.workType]) : null;
  const visibleLocation = job.locations[0] ?? null;
  const extraLocations = job.locations.length - 1;

  return (
    <div className="relative bg-white border border-[var(--object-strokes-dividers,#ccd4d1)] rounded-lg w-full">
      <Link
        href={job.viewJobPath}
        external={false}
        onClick={onViewJob}
        className={[
          "flex flex-col rounded-lg no-underline! text-inherit! hover:brightness-100! focus:brightness-100!",
          "hover:bg-[var(--pale-blue)]",
          compact ? "gap-2 p-4" : "gap-4 p-6",
        ].join(" ")}
      >
        <div className="flex gap-3 items-center pr-20">
          <CompanyLogo companyName={job.companyName} logoUrl={job.logoUrl} size="small" />
          <div className="flex flex-col min-w-0 flex-1">
            <p className="font-medium text-base leading-6 text-[var(--text-primary,#15372c)] truncate">
              {job.title}
            </p>
            <p className="text-sm leading-5 text-[var(--text-primary,#15372c)] truncate">
              {job.companyName}
            </p>
          </div>
        </div>

        <div className="flex flex-row gap-2 items-center flex-wrap">
          {workTypeLabel && (
            <Tag label={workTypeLabel} truncate={false} className="whitespace-nowrap" />
          )}
          {visibleLocation && (
            <Tag label={visibleLocation} truncate={false} className="whitespace-nowrap" />
          )}
          {extraLocations > 0 && (
            <span className="text-xs text-[var(--text-primary,#15372c)] whitespace-nowrap">
              {t("moreLocations", { count: extraLocations })}
            </span>
          )}
          {job.payRanges && (
            <Tag
              label={job.payRanges}
              truncate={false}
              color="var(--green-50)"
              className="whitespace-nowrap"
            />
          )}
        </div>

        {!compact && job.description && (
          <>
            <Divider />
            <div className="flex flex-col gap-2">
              <p className="text-sm leading-[18px] font-medium text-[var(--text-primary,#15372c)]">
                {t("roleDescription")}
              </p>
              <p className="text-sm leading-5 text-[var(--text-primary,#15372c)] line-clamp-3">
                {job.description}
              </p>
            </div>
          </>
        )}
      </Link>

      <div className={["absolute", compact ? "top-4 right-4" : "top-6 right-6"].join(" ")}>
        <RecommendationFeedbackButtons feedback={feedback} onChange={onFeedback} />
      </div>
    </div>
  );
};

export default JobRecommendationCard;
